import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '@environments/environment';
import { ActingAsAgent, ActingAsService } from '@services/acting-as.service';
import { SalesAssistantService } from '@services/api/sales-assistant.service';
import { AuthService } from '@services/auth.service';

/**
 * Top-bar Sales Assistant switcher (SALES_ASSISTANT_FRONTEND.md §4).
 *
 * Renders only for users whose role has `can_access_other_users`. Lets the assistant
 * pick an agent to act as, switch between agents, and exit back to their own account.
 * Detection is driven off `me` (via AuthService.UserSub); while acting-as the switcher
 * reads the cached agent list, since `me` then returns the agent (who is not an assistant).
 */
@Component({
  selector: 'app-agent-switcher',
  templateUrl: './agent-switcher.component.html',
  styleUrls: ['./agent-switcher.component.scss'],
})
export class AgentSwitcherComponent implements OnInit, OnDestroy {
  /** True when this user may act as other agents (assistant). Hides the whole control when false. */
  canAccess = false;
  /** True while impersonating an agent. */
  acting = false;
  /** Agent currently being impersonated (when acting). */
  currentAgent: ActingAsAgent | null = null;
  /** Agents this assistant may act as. */
  agents: ActingAsAgent[] = [];

  private s3 = environment.S3;
  private userSub?: Subscription;

  constructor(
    private auth: AuthService,
    private salesAssistant: SalesAssistantService,
    private actingAs: ActingAsService
  ) {}

  ngOnInit(): void {
    // Reflect a persisted acting-as session immediately (survives reload).
    this.applyActingState();

    // Re-evaluate whenever `me` is (re)loaded — that's when permissions become known.
    this.userSub = this.auth.UserSub.subscribe(user => this.evaluate(user));
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  /** Recompute switcher visibility/state from the latest `me` payload. */
  private evaluate(user: any): void {
    // While acting-as, `me` is the agent (no assistant permission) — rely on the cached list.
    if (this.actingAs.isActingAs()) {
      this.applyActingState();
      return;
    }

    this.acting = false;
    this.currentAgent = null;

    // UserSub is seeded with a boolean before the first `me` resolves; ignore non-user emissions.
    const permissions = user && typeof user === 'object' ? user?.permissions?.data : null;
    const canAccess = Array.isArray(permissions) && permissions.includes('can_access_other_users');
    if (!canAccess) {
      this.canAccess = false;
      this.agents = [];
      return;
    }

    this.canAccess = true;
    this.fetchAssignedAgents();
  }

  /** Hydrate the switcher from the persisted acting-as session + cached agent list. */
  private applyActingState(): void {
    if (!this.actingAs.isActingAs()) {
      return;
    }
    this.acting = true;
    this.canAccess = true;
    this.currentAgent = this.actingAs.getActingAgent();
    this.agents = this.actingAs.getAssignedAgentsCache();
  }

  /** Load (and cache) the assignable agents for this assistant. */
  private fetchAssignedAgents(): void {
    this.salesAssistant.getAssignedAgents().subscribe({
      next: response => {
        const data = response?.data;
        if (!data?.can_access_other_users) {
          this.canAccess = false;
          this.agents = [];
          return;
        }
        this.agents = data.agents || [];
        this.actingAs.persistAssignedAgents(this.agents);
      },
      error: () => {
        // Non-fatal — leave the switcher hidden/empty if the list can't be fetched.
      },
    });
  }

  /** Whether `agent` is the one currently being impersonated. */
  isCurrent(agent: ActingAsAgent): boolean {
    return this.acting && agent?._id === this.currentAgent?._id;
  }

  /** Agents must approve an assistant from their profile's Access tab before they can be acted as. */
  isApproved(agent: ActingAsAgent): boolean {
    return !agent?.access_status || agent.access_status === 'approved';
  }

  /** Pick an agent → act as them (no-op if already acting as that same agent). */
  pick(agent: ActingAsAgent): void {
    if (this.isCurrent(agent) || !this.isApproved(agent)) {
      return;
    }
    this.actingAs.setActiveAgent(agent);
  }

  /** Exit acting-as, back to the assistant's own account. */
  exit(): void {
    this.actingAs.exitActingAs();
  }

  /** Resolve an agent photo to a displayable URL (S3-relative or absolute). */
  photoSrc(agent: ActingAsAgent): string | null {
    const photo = agent?.photo;
    if (!photo) {
      return null;
    }
    return photo.startsWith('http') ? photo : `${this.s3}/${photo}`;
  }

  /** Hide a broken avatar image so the initials underneath show through. */
  onImgError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
    }
  }

  /** Two-letter initials fallback when an agent has no photo. */
  initials(agent: ActingAsAgent): string {
    const name = (agent?.full_name || '').trim();
    if (!name) {
      return '?';
    }
    const parts = name.split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }
}
